import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function EmailModal({ show, onClose, onSend, documentType, documentNumber, customerEmail, templateData }) {
  const [to, setTo] = useState(customerEmail || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const areaMap = {
    'Quote': 'quote',
    'Invoice': 'invoice',
    'Job': 'job',
    'Sales Order': 'sales_order'
  };

  useEffect(() => {
    if (show) {
      setTo(customerEmail || '');
      fetchTemplates();
    }
  }, [show, documentType, customerEmail]);

  const fetchTemplates = async () => {
    try {
      const area = areaMap[documentType] || 'quote';
      const response = await apiFetch(`/api/email-templates?area=${area}`);
      const data = await response.json();
      setTemplates(data);

      // Auto-select default template if available
      const defaultTemplate = data.find(t => t.is_default);
      if (defaultTemplate) {
        loadTemplate(defaultTemplate.id);
      } else if (data.length > 0) {
        loadTemplate(data[0].id);
      } else {
        // Fallback to basic template
        setSubject(`${documentType} ${documentNumber}`);
        setBody(`<p>Dear Customer,</p>

<p>Please find attached ${documentType} ${documentNumber}.</p>

<p>If you have any questions, please don't hesitate to contact us.</p>

<p>Best regards,<br>
<strong>[Your Company Name]</strong></p>`);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const loadTemplate = async (templateId) => {
    if (!templateId) return;

    setLoadingTemplate(true);
    setSelectedTemplateId(templateId);

    try {
      // Render template with actual data
      const response = await apiFetch('/api/email-templates/render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId,
          data: templateData || {}
        })
      });

      const { subject: renderedSubject, body: renderedBody } = await response.json();
      setSubject(renderedSubject);
      setBody(renderedBody);
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleSend = async () => {
    if (!to) {
      alert('Please enter recipient email address');
      return;
    }

    if (!subject) {
      alert('Please enter email subject');
      return;
    }

    setSending(true);
    setSendStatus('Preparing attachment and sending email...');
    try {
      await onSend({ to, subject, body });
      setSendStatus('');
      onClose();
    } catch (error) {
      console.error('Send failed:', error);
      setSendStatus('');
      alert(error.message || 'Email failed to send');
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Send Email</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl leading-none"
            disabled={sending}
          >
            x
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">

          {/* Template Selector */}
          {templates.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => loadTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                disabled={sending || loadingTemplate}
              >
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} {template.is_default && '(Default)'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* To */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="customer@example.com"
              disabled={sending}
            />
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Quote Q001"
              disabled={sending}
            />
          </div>

          {/* Body */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="Email body (HTML supported)"
              disabled={sending}
            />
            <p className="text-xs text-gray-500 mt-1">
              You can use HTML tags for formatting (e.g., &lt;p&gt;, &lt;strong&gt;, &lt;br&gt;)
            </p>
          </div>

          {/* Attachment info */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              <strong>Attachment:</strong> {documentType}-{documentNumber}.pdf will be attached to this email
            </p>
            {sendStatus && <p className="text-xs text-blue-700 mt-1">{sendStatus}</p>}
          </div>

        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default EmailModal;
